import React, { useState, useEffect, useCallback } from 'react';
import styled from 'libs/styled';
import useEmblaCarousel from 'embla-carousel-react';
import type { ContentSliderMobileProps } from './types';

const GAP_DEFAULT = 10;
const filterTransientProps = { shouldForwardProp: (prop: string) => !prop.startsWith('$') };

const SliderBleedWrapper = styled('div')`
    grid-column: 1 / -1;
    margin-left: calc(-1 * var(--grid-padding, 10px));
    margin-right: calc(-1 * var(--grid-padding, 10px));
    width: calc(100% + 2 * var(--grid-padding, 10px));
`;

const NoBleedWrapper = styled('div')`
    grid-column: 1 / -1;
`;

const EmblaViewport = styled('div')`
    overflow: hidden;
`;

const EmblaContainer = styled('div')`
    display: flex;
    align-items: flex-start;
    touch-action: pan-y pinch-zoom;
`;

const EmblaSlide = styled('div', filterTransientProps)<{
    $slideWidthPercent: number;
    $gap: number;
}>`
    flex: 0 0 ${({ $slideWidthPercent }) => $slideWidthPercent}%;
    min-width: 0;
    margin-right: ${({ $gap }) => $gap}px;
`;

const SlideInner = styled('div', filterTransientProps)<{
    $isActive: boolean;
    $position: 'left' | 'active' | 'right';
}>`
    transform: ${({ $isActive }) => ($isActive ? 'scale(1)' : 'scale(0.65)')};
    transform-origin: ${({ $position }) => {
        if ($position === 'left') return 'top right';
        if ($position === 'right') return 'top left';
        return 'top center';
    }};
    transition: transform 0.3s ease;
`;

const getSlidePosition = (index: number, selectedIndex: number, total: number): 'left' | 'active' | 'right' => {
    if (index === selectedIndex) return 'active';
    const diff = (((index - selectedIndex) % total) + total) % total;
    return diff <= total / 2 ? 'right' : 'left';
};

/** Mobile touch carousel (Embla). Exposes nav API via onReady for SliderHeader. */
const ContentSliderMobile = <T,>({
    items,
    getItemKey,
    renderSlide,
    slideWidthPercent = 63,
    gap = GAP_DEFAULT,
    bleed = true,
    onReady,
}: ContentSliderMobileProps<T>) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: 'center',
        loop: items.length > 1,
        containScroll: false,
    });
    const [selectedIndex, setSelectedIndex] = useState(0);

    const scrollPrev = useCallback(() => {
        emblaApi?.scrollPrev();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
        emblaApi?.scrollNext();
    }, [emblaApi]);

    const canGoPrev = items.length > 1;
    const canGoNext = items.length > 1;

    useEffect(() => {
        if (!emblaApi) return;

        const onSelect = () => {
            setSelectedIndex(emblaApi.selectedScrollSnap());
        };

        emblaApi.on('select', onSelect);
        onSelect();

        return () => {
            emblaApi.off('select', onSelect);
        };
    }, [emblaApi]);

    useEffect(() => {
        onReady?.({
            scrollPrev,
            scrollNext,
            canGoPrev,
            canGoNext,
        });
    }, [onReady, scrollPrev, scrollNext, canGoPrev, canGoNext]);

    if (items.length === 0) {
        return null;
    }

    const Wrapper = bleed ? SliderBleedWrapper : NoBleedWrapper;

    return (
        <Wrapper>
            <EmblaViewport ref={emblaRef}>
                <EmblaContainer>
                    {items.map((item, index) => (
                        <EmblaSlide key={getItemKey(item)} $slideWidthPercent={slideWidthPercent} $gap={gap}>
                            <SlideInner
                                $isActive={index === selectedIndex}
                                $position={getSlidePosition(index, selectedIndex, items.length)}
                            >
                                {renderSlide(item, index)}
                            </SlideInner>
                        </EmblaSlide>
                    ))}
                </EmblaContainer>
            </EmblaViewport>
        </Wrapper>
    );
};

export default ContentSliderMobile;
export type { ContentSliderMobileProps };
